import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Button, TextField, Select, MenuItem,
    Card, CardContent, Avatar, Grid, InputAdornment, CircularProgress,
    Divider, IconButton, Collapse, Chip
} from '@mui/material';
import { useSelector } from 'react-redux';
import { FinanceService } from '../../../api/services/financeService';
import { SettingsService } from '../../../api/services/settingsService';
import { toast } from '../../../utils/toast';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { formatCurrency } from '../../../utils/currency';

export default function CashierShiftsUI() {
    const { user } = useSelector((state) => state.auth || {});
    const activeTenantId = user?.tenantId;
    const [activeTab, setActiveTab] = useState('open'); // 'open' | 'close'
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [hasActiveShift, setHasActiveShift] = useState(false);

    const [currentUser, setCurrentUser] = useState({ initials: '', name: '', role: 'Cashier' });

    // --- Open Shift State ---
    const [outlet, setOutlet] = useState('Front desk');
    const [shiftStart, setShiftStart] = useState(() => {
        const now = new Date();
        return now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    });
    const [openingFloat, setOpeningFloat] = useState(0);

    // --- Close Shift State ---
    const [shifts, setShifts] = useState([]);
    const [expandedShifts, setExpandedShifts] = useState({});
    const [countedAmounts, setCountedAmounts] = useState({});

    useEffect(() => {
        fetchOpenShifts();
    }, [activeTab]);

    useEffect(() => {
        if (activeTab === 'open') {
            const now = new Date();
            setShiftStart(now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }));
            setOpeningFloat(0);
        }
    }, [activeTab]);

    const fetchOpenShifts = async () => {
        setIsLoading(true);
        try {
            const authData = JSON.parse(localStorage.getItem('adminAuth') || '{}');
            const tenantId = activeTenantId || authData.tenantId || null;
            let data = [];
            try {
                const [responseData, methodsRes] = await Promise.all([
                    FinanceService.getOpenCashierShifts(tenantId),
                    SettingsService.getPaymentMethods(tenantId, { noPagination: true }).catch(err => {
                        console.warn("Failed to fetch payment methods:", err);
                        return [];
                    })
                ]);

                const methodsList = Array.isArray(methodsRes?.data) ? methodsRes.data : (Array.isArray(methodsRes) ? methodsRes : []);
                const activeMethods = methodsList.filter(m => m.enabled === undefined || m.enabled === true || m.status === 'Active');

                const findExpected = (totals, name) => {
                    if (!totals || typeof totals !== 'object') return 0;
                    if (totals[name] !== undefined) return Number(totals[name]) || 0;
                    const lower = name.toLowerCase();
                    const matchedKey = Object.keys(totals).find(k => k.toLowerCase() === lower);
                    if (matchedKey && totals[matchedKey] !== undefined) {
                        return Number(totals[matchedKey]) || 0;
                    }
                    return 0;
                };

                const shiftsToMap = responseData?.shifts || (Array.isArray(responseData) ? responseData : []);

                if (responseData?.active !== undefined) {
                    setHasActiveShift(responseData.active);
                }

                if (responseData?.username) {
                    const name = responseData.username.trim();
                    const nameParts = name.split(' ');
                    const initials = nameParts.length > 1 ? nameParts[0][0] + nameParts[nameParts.length - 1][0] : name.substring(0, 2);
                    setCurrentUser({
                        initials: initials.toUpperCase(),
                        name: name,
                        role: responseData.shift?.outlet || responseData.outlet || ''
                    });
                }

                if (shiftsToMap.length > 0) {
                    data = shiftsToMap.map(shift => {
                        const name = shift.user?.name || 'Unknown';
                        const nameParts = name.trim().split(' ');
                        const initials = nameParts.length > 1 ? nameParts[0][0] + nameParts[nameParts.length - 1][0] : name.substring(0, 2);

                        let start = '00:00';
                        if (shift.openedAt) {
                            start = new Date(shift.openedAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
                        }
                        const end = shift.closedAt ? new Date(shift.closedAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : 'Present';

                        const payments = [];
                        const addedNames = new Set();

                        // 1. Add active payment methods from API
                        activeMethods.forEach(m => {
                            const methodName = m.name || m.label || (typeof m === 'string' ? m : '');
                            if (methodName) {
                                payments.push({
                                    method: methodName,
                                    expected: findExpected(shift.expectedTotals, methodName)
                                });
                                addedNames.add(methodName.toLowerCase());
                            }
                        });

                        // 2. Also include any remaining methods from shift.expectedTotals not in active methods
                        if (shift.expectedTotals) {
                            Object.keys(shift.expectedTotals).forEach(key => {
                                if (key !== 'Other' && !addedNames.has(key.toLowerCase())) {
                                    payments.push({
                                        method: key,
                                        expected: Number(shift.expectedTotals[key]) || 0
                                    });
                                    addedNames.add(key.toLowerCase());
                                }
                            });
                        }

                        return {
                            id: shift.id,
                            initials: initials.toUpperCase(),
                            name: name,
                            role: shift.outlet || 'Front desk',
                            timeSpan: `${start}–${end}`,
                            status: shift.status || 'Open',
                            openingFloat: Number(shift.openingFloat) || 0,
                            salesTaken: Number(shift.salesTaken) || 0,
                            expectedCash: Number(shift.expectedCash) || 0,
                            payments: payments
                        };
                    });
                }
            } catch (apiError) {
                console.warn("Failed to fetch API data:", apiError);
            }

            setShifts(data);

            // Initialize expanded state and counted amounts state
            const initialExpanded = {};
            const initialCounted = {};
            data.forEach(shift => {
                initialExpanded[shift.id] = true;
                initialCounted[shift.id] = {};
                shift.payments.forEach(p => {
                    initialCounted[shift.id][p.method] = '';
                });
            });
            setExpandedShifts(initialExpanded);
            setCountedAmounts(initialCounted);

        } catch (error) {
            console.error("Failed to fetch open shifts", error);
            toast.error("Failed to load active shifts.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleOpenShift = async () => {
        setIsSubmitting(true);
        try {
            const authData = JSON.parse(localStorage.getItem('adminAuth') || '{}');
            const tenantId = activeTenantId || authData.tenantId || null;

            await FinanceService.openCashierShift(tenantId, {
                outlet,
                openingFloat: Number(openingFloat)
            });

            toast.success("Cashier shift opened successfully!");
            setOpeningFloat(0);
            fetchOpenShifts();
            setActiveTab('close'); // Switch to close tab to see it
        } catch (error) {
            console.error("Failed to open shift", error);
            toast.error("Failed to open cashier shift.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCloseShift = async (shiftId) => {
        setIsSubmitting(true);
        try {
            const authData = JSON.parse(localStorage.getItem('adminAuth') || '{}');
            const tenantId = activeTenantId || authData.tenantId || null;

            const counts = countedAmounts[shiftId] || {};
            const tenders = Object.entries(counts).map(([method, amount]) => ({
                tenderType: method === 'Bank transfer' ? 'Bank Transfer' : method,
                declaredTotal: Number(amount) || 0
            }));

            const payload = {
                shiftId,
                tenders
            };

            await FinanceService.closeCashierShift(tenantId, payload);
            toast.success("Cashier shift declared and closed!");
            fetchOpenShifts(); // Refresh list
        } catch (error) {
            console.error("Failed to close shift", error);
            toast.error("Failed to declare cashier shift.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCountChange = (shiftId, method, value) => {
        setCountedAmounts(prev => ({
            ...prev,
            [shiftId]: {
                ...prev[shiftId],
                [method]: value
            }
        }));
    };

    const toggleShiftExpand = (shiftId) => {
        setExpandedShifts(prev => ({ ...prev, [shiftId]: !prev[shiftId] }));
    };

    // Calculate global summaries
    const calculateGlobals = () => {
        let totalExpected = 0;
        let totalCounted = 0;

        shifts.forEach(shift => {
            shift.payments.forEach(p => {
                totalExpected += Number(p.expected || 0);
                const countedStr = countedAmounts[shift.id]?.[p.method];
                if (countedStr) {
                    totalCounted += Number(countedStr);
                }
            });
        });

        const variance = totalCounted - totalExpected;

        return { totalExpected, totalCounted, variance };
    };

    const { totalExpected, totalCounted, variance } = calculateGlobals();

    return (
        <Box sx={{ maxWidth: 900, margin: '0 auto', p: 3 }}>
            {/* Header */}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                Night audit processing
            </Typography>
            <Typography variant="h5" fontWeight="bold" sx={{ mb: 4, color: '#111827' }}>
                Cashier shifts
            </Typography>

            {/* Tab Navigation */}
            <Box sx={{ display: 'flex', gap: 2, mb: 4 }}>
                <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => setActiveTab('open')}
                    sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                        py: 1.2,
                        borderRadius: 2,
                        color: activeTab === 'open' ? '#6d28d9' : 'text.secondary',
                        borderColor: activeTab === 'open' ? '#8b5cf6' : '#e5e7eb',
                        bgcolor: activeTab === 'open' ? '#f5f3ff' : 'transparent',
                        '&:hover': {
                            bgcolor: activeTab === 'open' ? '#ede9fe' : '#f9fafb',
                            borderColor: activeTab === 'open' ? '#7c3aed' : '#d1d5db'
                        }
                    }}
                >
                    Open shift
                </Button>
                <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => setActiveTab('close')}
                    sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                        py: 1.2,
                        borderRadius: 2,
                        color: activeTab === 'close' ? '#6d28d9' : 'text.secondary',
                        borderColor: activeTab === 'close' ? '#8b5cf6' : '#e5e7eb',
                        bgcolor: activeTab === 'close' ? '#f5f3ff' : 'transparent',
                        '&:hover': {
                            bgcolor: activeTab === 'close' ? '#ede9fe' : '#f9fafb',
                            borderColor: activeTab === 'close' ? '#7c3aed' : '#d1d5db'
                        }
                    }}
                >
                    Close shift
                </Button>
            </Box>

            {/* View Content */}
            {activeTab === 'open' && (
                <Card variant="outlined" sx={{ borderRadius: 3, borderColor: '#e5e7eb' }}>
                    <CardContent sx={{ p: 4 }}>
                        {/* User Profile */}
                        {hasActiveShift && currentUser.name && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
                                <Avatar sx={{ bgcolor: '#f3e8ff', color: '#6d28d9', fontWeight: 'bold' }}>
                                    {currentUser.initials}
                                </Avatar>
                                <Box>
                                    <Typography variant="subtitle1" fontWeight="bold" sx={{ color: '#111827' }}>
                                        {currentUser.name}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {currentUser.role || outlet}
                                    </Typography>
                                </Box>
                            </Box>
                        )}

                        {hasActiveShift ? (
                            <Box sx={{ p: 4, textAlign: 'center', bgcolor: '#f9fafb', borderRadius: 3, border: '1px dashed #d1d5db' }}>
                                <Typography variant="body1" color="text.secondary">
                                    You already have an active shift. Please close it before opening a new one.
                                </Typography>
                            </Box>
                        ) : (
                            <Box>
                                <Grid container spacing={3} sx={{ mb: 3 }}>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 500 }}>
                                            Outlet
                                        </Typography>
                                        <Select
                                            fullWidth
                                            size="small"
                                            value={outlet}
                                            onChange={(e) => setOutlet(e.target.value)}
                                            sx={{ borderRadius: 2 }}
                                        >
                                            <MenuItem value="Front desk">Front desk</MenuItem>
                                            <MenuItem value="Restaurant">Restaurant</MenuItem>
                                            <MenuItem value="Spa">Spa</MenuItem>
                                        </Select>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 500 }}>
                                            Shift start
                                        </Typography>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            type="time"
                                            disabled
                                            value={shiftStart}
                                            onChange={(e) => setShiftStart(e.target.value)}
                                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                        />
                                    </Grid>
                                </Grid>

                                <Box sx={{ mb: 4 }}>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 500 }}>
                                        Opening float
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        type="number"
                                        placeholder="Enter starting cash amount"
                                        value={openingFloat}
                                        onChange={(e) => {
                                            let val = e.target.value;
                                            if (val.length > 1 && val.startsWith('0') && !val.startsWith('0.')) {
                                                val = val.replace(/^0+/, '');
                                            }
                                            setOpeningFloat(val === '' ? 0 : val);
                                        }}
                                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                    />
                                </Box>

                                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                    <Button
                                        variant="contained"
                                        onClick={handleOpenShift}
                                        disabled={isSubmitting}
                                        sx={{
                                            bgcolor: '#d8b4fe',
                                            color: '#4c1d95',
                                            fontWeight: 600,
                                            textTransform: 'none',
                                            borderRadius: 2,
                                            px: 4,
                                            py: 1,
                                            boxShadow: 'none',
                                            '&:hover': { bgcolor: '#c084fc', boxShadow: 'none' }
                                        }}
                                    >
                                        {isSubmitting ? <CircularProgress size={24} color="inherit" /> : 'Open shift'}
                                    </Button>
                                </Box>
                            </Box>
                        )}
                    </CardContent>
                </Card>
            )}

            {activeTab === 'close' && (
                <Box>
                    {/* Global Summary Cards */}
                    <Grid container spacing={2} sx={{ mb: 4 }}>
                        <Grid item xs={12} sm={4}>
                            <Card variant="outlined" sx={{ borderRadius: 2, borderColor: '#e5e7eb' }}>
                                <CardContent sx={{ p: '16px !important' }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                        Total expected
                                    </Typography>
                                    <Typography variant="h5" fontWeight="bold" sx={{ mt: 0.5, color: '#111827' }}>
                                        {formatCurrency(totalExpected)}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <Card variant="outlined" sx={{ borderRadius: 2, borderColor: '#e5e7eb' }}>
                                <CardContent sx={{ p: '16px !important' }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                        Total counted
                                    </Typography>
                                    <Typography variant="h5" fontWeight="bold" sx={{ mt: 0.5, color: '#111827' }}>
                                        {formatCurrency(totalCounted)}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <Card variant="outlined" sx={{ borderRadius: 2, borderColor: '#e5e7eb' }}>
                                <CardContent sx={{ p: '16px !important' }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                        Total variance
                                    </Typography>
                                    <Typography variant="h5" fontWeight="bold" sx={{ mt: 0.5, color: variance === 0 ? '#111827' : (variance > 0 ? '#10b981' : '#ef4444') }}>
                                        {variance < 0 ? '-' : ''}{formatCurrency(Math.abs(variance))}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>

                    {/* Active Shifts */}
                    {isLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                            <CircularProgress sx={{ color: '#8b5cf6' }} />
                        </Box>
                    ) : shifts.length === 0 ? (
                        <Typography color="text.secondary" align="center" sx={{ p: 4 }}>
                            No active shifts to close.
                        </Typography>
                    ) : (
                        shifts.map(shift => (
                            <Card key={shift.id} variant="outlined" sx={{ mb: 3, borderRadius: 3, borderColor: '#e5e7eb' }}>
                                <CardContent sx={{ p: 0 }}>
                                    {/* Shift Header */}
                                    <Box
                                        sx={{
                                            p: 3,
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            cursor: 'pointer'
                                        }}
                                        onClick={() => toggleShiftExpand(shift.id)}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <Avatar sx={{ bgcolor: '#f3e8ff', color: '#6d28d9', fontWeight: 'bold' }}>
                                                {shift.initials}
                                            </Avatar>
                                            <Box>
                                                <Typography variant="subtitle1" fontWeight="bold" sx={{ color: '#111827', lineHeight: 1.2 }}>
                                                    {shift.name}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {shift.role} · {shift.timeSpan}
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    bgcolor: shift.status === 'Closed' ? '#dcfce7' : '#fef3c7',
                                                    color: shift.status === 'Closed' ? '#166534' : '#92400e',
                                                    px: 1.5,
                                                    py: 0.5,
                                                    borderRadius: 1,
                                                    fontWeight: 600
                                                }}
                                            >
                                                {shift.status}
                                            </Typography>
                                            <IconButton size="small">
                                                {expandedShifts[shift.id] ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                            </IconButton>
                                        </Box>
                                    </Box>

                                    <Collapse in={expandedShifts[shift.id]}>
                                        {/* Summary Row */}
                                        <Box sx={{ px: 3, pb: 2 }}>
                                            <Grid container spacing={2}>
                                                <Grid item xs={4}>
                                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                                        Opening float
                                                    </Typography>
                                                    <Typography variant="body2" fontWeight="600" color="#111827">
                                                        {formatCurrency(shift.openingFloat)}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={4}>
                                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                                        Sales taken
                                                    </Typography>
                                                    <Typography variant="body2" fontWeight="600" color="#111827">
                                                        {formatCurrency(shift.salesTaken)}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={4}>
                                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                                        Expected cash
                                                    </Typography>
                                                    <Typography variant="body2" fontWeight="600" color="#111827">
                                                        {formatCurrency(shift.expectedCash)}
                                                    </Typography>
                                                </Grid>
                                            </Grid>
                                            <Divider sx={{ mt: 3, borderColor: '#e5e7eb' }} />
                                        </Box>

                                        {/* Declarations */}
                                        <Box sx={{ px: 3, pb: 3 }}>
                                            {shift.payments.map((payment, idx) => {
                                                const getIcon = (method) => {
                                                    const m = (method || '').toLowerCase();
                                                    if (m === 'cash') return <AccountBalanceWalletIcon sx={{ color: '#10b981' }} />;
                                                    if (m === 'card') return <CreditCardIcon sx={{ color: '#eab308' }} />;
                                                    if (m === 'upi') return <QrCodeScannerIcon sx={{ color: '#0f172a' }} />;
                                                    return <AccountBalanceIcon sx={{ color: '#64748b' }} />;
                                                };

                                                return (
                                                    <Box key={idx} sx={{ display: 'flex', alignItems: 'center', mb: 2, justifyContent: 'space-between' }}>
                                                        <Box sx={{ width: 150, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                            {getIcon(payment.method)}
                                                            <Box>
                                                                <Typography variant="body2" fontWeight="600" color="#111827" sx={{ lineHeight: 1.2 }}>
                                                                    {payment.method}
                                                                </Typography>
                                                                {shift.status !== 'Closed' && (
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        Expected<br />{formatCurrency(payment.expected)}
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                        </Box>
                                                        {shift.status === 'Closed' ? (
                                                            <Typography variant="body2" fontWeight="600" color="#111827">
                                                                {formatCurrency(payment.counted)}
                                                            </Typography>
                                                        ) : (
                                                            <Box sx={{ width: '100%', maxWidth: 280, display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                                                <TextField
                                                                    fullWidth
                                                                    size="small"
                                                                    placeholder="Counted"
                                                                    type="number"
                                                                    value={countedAmounts[shift.id]?.[payment.method] || ''}
                                                                    onChange={(e) => handleCountChange(shift.id, payment.method, e.target.value)}
                                                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                                                />
                                                                {(payment.method === 'Cash' || payment.method?.toLowerCase() === 'cash') && countedAmounts[shift.id]?.[payment.method] !== '' && countedAmounts[shift.id]?.[payment.method] !== undefined && (() => {
                                                                    const declaredCash = Number(countedAmounts[shift.id]?.[payment.method] || 0);
                                                                    const expectedCash = Number(shift.expectedCash || payment.expected || 0);
                                                                    const cashVariance = declaredCash - expectedCash;
                                                                    if (cashVariance === 0) {
                                                                        return (
                                                                            <Chip 
                                                                                size="small" 
                                                                                label="Balanced (±0.00)" 
                                                                                sx={{ 
                                                                                    bgcolor: '#f0fdf4', 
                                                                                    color: '#15803d', 
                                                                                    fontWeight: 700, 
                                                                                    fontSize: '11px', 
                                                                                    height: 22, 
                                                                                    border: '1px solid #bbf7d0',
                                                                                    mt: 0.75 
                                                                                }} 
                                                                            />
                                                                        );
                                                                    }
                                                                    if (cashVariance > 0) {
                                                                        return (
                                                                            <Chip 
                                                                                size="small" 
                                                                                label={`Cash Overage (+${formatCurrency(cashVariance)})`} 
                                                                                sx={{ 
                                                                                    bgcolor: '#fffbeb', 
                                                                                    color: '#b45309', 
                                                                                    fontWeight: 700, 
                                                                                    fontSize: '11px', 
                                                                                    height: 22, 
                                                                                    border: '1px solid #fde68a',
                                                                                    mt: 0.75 
                                                                                }} 
                                                                            />
                                                                        );
                                                                    }
                                                                    return (
                                                                        <Chip 
                                                                            size="small" 
                                                                            label={`Cash Shortage (-${formatCurrency(Math.abs(cashVariance))})`} 
                                                                            sx={{ 
                                                                                bgcolor: '#fef2f2', 
                                                                                color: '#b91c1c', 
                                                                                fontWeight: 700, 
                                                                                fontSize: '11px', 
                                                                                height: 22, 
                                                                                border: '1px solid #fecaca',
                                                                                mt: 0.75 
                                                                            }} 
                                                                        />
                                                                    );
                                                                })()}
                                                            </Box>
                                                        )}
                                                    </Box>
                                                );
                                            })}

                                            {shift.status === 'Closed' ? (
                                                <Box sx={{ borderTop: '1px solid #e5e7eb', mt: 3, pt: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                    <Box>
                                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                            Total counted
                                                        </Typography>
                                                        <Typography variant="body2" fontWeight="bold" color="#111827">
                                                            {formatCurrency(shift.totalCounted)}
                                                        </Typography>
                                                    </Box>
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            bgcolor: shift.isMatched ? '#dcfce7' : '#fee2e2',
                                                            color: shift.isMatched ? '#166534' : '#991b1b',
                                                            px: 1.5,
                                                            py: 0.5,
                                                            borderRadius: 1,
                                                            fontWeight: 600
                                                        }}
                                                    >
                                                        {shift.isMatched ? 'Matched' : 'Variance'}
                                                    </Typography>
                                                </Box>
                                            ) : (
                                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                                                    <Button
                                                        variant="contained"
                                                        onClick={() => handleCloseShift(shift.id)}
                                                        disabled={isSubmitting}
                                                        sx={{
                                                            bgcolor: '#d1d5db',
                                                            color: '#4b5563',
                                                            fontWeight: 600,
                                                            textTransform: 'none',
                                                            borderRadius: 2,
                                                            px: 4,
                                                            py: 1,
                                                            boxShadow: 'none',
                                                            '&:hover': { bgcolor: '#9ca3af', boxShadow: 'none' }
                                                        }}
                                                    >
                                                        {isSubmitting ? <CircularProgress size={24} color="inherit" /> : 'Close shift'}
                                                    </Button>
                                                </Box>
                                            )}
                                        </Box>
                                    </Collapse>
                                </CardContent>
                            </Card>
                        ))
                    )}
                </Box>
            )}
        </Box>
    );
}
